/**
 * SelectUda
 * First step of the sign-in cascade: person → UDA → role.
 *
 * An employee can belong to more than one Urban Development Authority. In the database that
 * is not one account with two jurisdictions — ds_usr_lst_t is keyed (username, tenantid), so
 * the same person holds a separate row per UDA, each with its own positions, assignments and
 * data. Choosing a UDA here therefore decides which of those identities the session runs as,
 * which is why it must be answered before the role step.
 *
 * Picking one posts to /select-tenant, which re-resolves the person inside that UDA and
 * re-issues the token. From there the cascade continues: more than one role in the chosen
 * UDA sends the user to the role picker, otherwise straight to that UDA's landing page.
 *
 * Employees of a single UDA never see this screen — login goes straight through.
 * State and Regional users never see it either; they work across UDAs by design and pick
 * one from the header instead.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, redirectToUserMainPage, type Tenant } from '../../../../context/AuthContext';
import AuthShell from '../components/AuthShell';

/**
 * UDA names arrive as "Rajahmundry Urban Development Authority  (RUDA)" — the authority and
 * its acronym in one string. Split them so the card can lead with the short code and keep the
 * full name as the supporting line.
 */
const splitUdaName = (name: string, udaCode?: string | null): { code: string | null; label: string } => {
  // The server now sends the UDA name (ds_uda_lst_t) with its code beside it; the bracket
  // form is only for a name that still carries one.
  if (udaCode) return { code: udaCode, label: (name || '').replace(/\s{2,}/g, ' ').trim() };
  const match = /^(.*?)\s*\(([^)]+)\)\s*$/.exec((name || '').trim());
  if (!match) return { code: null, label: (name || '').replace(/\s{2,}/g, ' ').trim() };
  return { code: match[2].trim(), label: match[1].replace(/\s{2,}/g, ' ').trim() };
};

const SelectUda: React.FC = () => {
  const { user, selectUda, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [submittingId, setSubmittingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const udas: Tenant[] = user?.tenants || [];

  // Guard the route directly: arriving here without a session, or with nothing to choose
  // between, must never strand the user on an empty screen.
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
      return;
    }
    if (udas.length < 2) {
      redirectToUserMainPage();
    }
  }, [isAuthenticated, udas.length, navigate]);

  const handleSelect = async (tenantId: number) => {
    if (submittingId !== null) return; // the page navigates away on success; ignore double-clicks
    setError(null);
    setSubmittingId(tenantId);
    try {
      await selectUda(tenantId);
      // selectUda performs a full document load into the role picker or the landing page.
    } catch (e: any) {
      setError(e?.message || 'Could not open that UDA. Please try again.');
      setSubmittingId(null);
    }
  };

  if (!isAuthenticated || udas.length < 2) {
    return null;
  }

  return (
    <AuthShell bandLabel="DTCP ERP · Select UDA">
      <div className="text-center mb-6">
        <p className="text-[11px] sm:text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 dark:text-gray-400">
          {user?.name ? `Signed in as ${user.name}` : 'Signed in'}
        </p>
        <h2 className="mt-1 text-xl sm:text-2xl font-bold tracking-tight animated-gradient-text">
          Select your UDA
        </h2>
        <div className="mx-auto mt-3 h-1 w-16 rounded-full bg-gradient-to-r from-amber-500 to-amber-600"></div>
        <p className="mt-3 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
          You work at more than one authority. Choose the one you want to open — each has its
          own positions, data and records.
        </p>
      </div>

      {error && (
        <div
          role="alert"
          className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300"
        >
          {error}
        </div>
      )}

      <ul className="space-y-3">
        {udas.map((uda) => {
          const { code, label } = splitUdaName(uda.name, uda.udaCode);
          const isSubmitting = submittingId === uda.id;
          const isDisabled = submittingId !== null && !isSubmitting;
          return (
            <li key={uda.id}>
              <button
                type="button"
                onClick={() => handleSelect(uda.id)}
                disabled={submittingId !== null}
                aria-busy={isSubmitting}
                className={`w-full text-left rounded-xl border p-4 transition
                  border-slate-200 dark:border-gray-700
                  bg-white dark:bg-gray-900/40
                  hover:border-amber-500 hover:shadow-md
                  focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1
                  ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}
                  ${isSubmitting ? 'border-amber-500 shadow-md' : ''}`}
              >
                <div className="flex items-center gap-3">
                  <span className="shrink-0 flex h-10 w-10 items-center justify-center rounded-lg bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                    <i className="ri-government-line text-lg" aria-hidden="true"></i>
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block font-semibold text-slate-900 dark:text-gray-100 truncate">
                      {code || label}
                    </span>
                    {code && (
                      <span className="block text-xs text-gray-600 dark:text-gray-400 truncate">
                        {label}
                      </span>
                    )}
                  </span>
                  <span className="shrink-0 text-amber-600 dark:text-amber-400">
                    {isSubmitting ? (
                      <i className="ri-loader-4-line animate-spin text-lg" aria-hidden="true"></i>
                    ) : (
                      <i className="ri-arrow-right-line text-lg" aria-hidden="true"></i>
                    )}
                  </span>
                </div>
              </button>
            </li>
          );
        })}
      </ul>

      <p className="mt-6 text-center text-xs text-gray-400 dark:text-gray-500">
        You can switch UDA later from the header without signing out.
      </p>
    </AuthShell>
  );
};

export default SelectUda;
